import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMotionPolicy } from "../lib/hooks/useMotionPolicy";
import { usePresentedSnapshot } from "../presentation/hooks";
import { focusTargetEntityId, useNexusStore } from "../store/nexusStore";

export function CinematicFocusTransition() {
  const focus = useNexusStore((state) => state.focus);
  const demoTourActive = useNexusStore((state) => state.demoTourActive);
  const snapshot = usePresentedSnapshot();
  const motionPolicy = useMotionPolicy();
  const previous = useRef<string | null>(null);
  const [active, setActive] = useState(false);
  const id = focusTargetEntityId(focus);
  const blackHole = id ? snapshot?.projection.nodes[id]?.archetype === "black-hole" : false;

  const target = useMemo(() => {
    if (!id || !snapshot) return null;
    return (
      snapshot.nodes.find((node) => node.id === id) ??
      snapshot.orbitalNodes.find((orbital) => orbital.id === id) ??
      null
    );
  }, [id, snapshot]);

  useEffect(() => {
    if (motionPolicy.reduced || !id || id === previous.current) {
      previous.current = id;
      return;
    }
    previous.current = id;
    setActive(true);
    const duration = demoTourActive ? (blackHole ? 2300 : 1500) : blackHole ? 2050 : 980;
    const timer = window.setTimeout(() => setActive(false), duration);
    return () => window.clearTimeout(timer);
  }, [blackHole, demoTourActive, id, motionPolicy.reduced]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          className={`cinematic-focus${blackHole ? " is-singularity" : ""}${demoTourActive ? " is-demo-tour" : ""}`}
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: demoTourActive ? 0.42 : 0.22 }}
        >
          <span className="cinematic-focus__veil" />
          <span className="cinematic-focus__iris" />
          <span className="cinematic-focus__ring cinematic-focus__ring--a" />
          <span className="cinematic-focus__ring cinematic-focus__ring--b" />
          <span className="cinematic-focus__scan" />
          <span className="cinematic-focus__streak cinematic-focus__streak--a" />
          <span className="cinematic-focus__streak cinematic-focus__streak--b" />
          {demoTourActive && (
            <>
              <span className="cinematic-focus__matte cinematic-focus__matte--top" />
              <span className="cinematic-focus__matte cinematic-focus__matte--bottom" />
              <span className="cinematic-focus__glint" />
              {target && (
                <span className="cinematic-focus__target-caption">
                  {target.eyebrow} / {target.label}
                </span>
              )}
            </>
          )}
          {!demoTourActive && blackHole && <span className="cinematic-focus__caption">GRAVITATIONAL VECTOR LOCK</span>}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
